import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from "@/components/ui/table";

interface Seance {
  sujet: string;
  fiable: boolean;
  sessionDate: string;
  // @ts-expect-error // Ignore TS error for dynamic properties
  parameters: { content: string }[];
  resultsOB?: string | null;
  resultsOT?: string | null;
  resultsPVS?: string | null;
  resultsZPCCF?: string | null;
  resultsZPFCF?: string | null;
  resultsZPI?: string | null;
  resultsZPL?: string | null;
  order: number;
  accountUsed: "PATIENT" | "CLINIQUE";
  screenUsed: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface FilteredParams {
  content: { [key: string]: string };
  availableParameters: string[];
  result: string;
}

const SeanceTable = ({
  seances,
  selectedExercise,
}: {
  seances: Seance[];
  selectedExercise: string;
}) => {
  const getDynamicParameters = (
    session: Seance,
    selectedExercise: string
  ): FilteredParams => {
    const sous_module = selectedExercise.replace("results", "");

    const filteredParam = session.parameters.find(
      (param) => JSON.parse(param.content).sous_module === sous_module
    );

    if (filteredParam) {
      const content = JSON.parse(filteredParam.content);
      const availableParameters = [
        "date",
        "order",
        "accountUsed", 
        "screenUsed",
        ...Object.keys(content).filter(
          (key) => key !== "sous_module" && key !== "selected"
        ),
      ];

      const result: string =
        typeof session[selectedExercise] === "string"
          ? (session[selectedExercise] as string)
          : "N/A";

      return {
        content: { 
          date: session.sessionDate, 
          order: session.order.toString(),
          accountUsed: session.accountUsed,
          screenUsed: session.screenUsed,
          ...content 
        },
        availableParameters,
        result,
      };
    }

    return {
      content: { 
        date: session.sessionDate,
        order: session.order.toString(),
        accountUsed: session.accountUsed,
        screenUsed: session.screenUsed
      },
      availableParameters: ["date", "order", "accountUsed", "screenUsed"],
      result: "N/A",
    };
  };

  const uniqueParameters = new Set<string>();

  const filteredSeances = seances
    .sort(
      (a, b) =>
        new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime()
    )
    .map((session) => {
      const dynamicParams = getDynamicParameters(session, selectedExercise);
      Object.keys(dynamicParams.content).forEach((param) =>
        uniqueParameters.add(param)
      );
      return { session, dynamicParams };
    });

  const uniqueParamsArray = Array.from(uniqueParameters);

  // Helper function to format values
  const formatValue = (key: string, value: string) => {
    if (key === "date") {
      return new Date(value).toLocaleDateString('fr-FR');
    }
    if (key === "accountUsed") {
      return value === "PATIENT" ? "Patient" : "Clinique";
    }
    if (key === "fiable") {
      return value === "true" ? "Oui" : "Non";
    }
    return value;
  };

  // Helper function to get parameter label
  const getParameterLabel = (param: string) => {
    const labels: { [key: string]: string } = {
      date: "Date",
      order: "Ordre", 
      accountUsed: "Compte",
      screenUsed: "Écran",
      fiable: "Fiable",
    };
    return labels[param] || param;
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-full">
        <Table className="border-collapse border border-gray-300">
          <TableHeader>
            <TableRow className="bg-gray-100">
              <TableHead className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-700 min-w-[120px]">
                Paramètres
              </TableHead>
              {filteredSeances.map(({ session }, index) => (
                <TableHead 
                  key={session.order} 
                  className="border border-gray-300 px-3 py-2 text-center font-semibold text-gray-700 min-w-[100px] bg-blue-50"
                >
                  <div className="flex flex-col">
                    <span className="text-xs text-blue-600">Séance {index + 1}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(session.sessionDate).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Result row - highlighted */}
            <TableRow className="bg-yellow-50 font-medium">
              <TableCell className="border border-gray-300 px-3 py-2 font-semibold text-gray-800">
                Résultat
              </TableCell>
              {filteredSeances.map(({ dynamicParams, session }) => (
                <TableCell 
                  key={session.order} 
                  className="border border-gray-300 px-3 py-2 text-center font-bold text-blue-600"
                >
                  {dynamicParams.result}
                </TableCell>
              ))}
            </TableRow>
            
            {/* Fiability row */}
            <TableRow>
              <TableCell className="border border-gray-300 px-3 py-2 font-medium text-gray-700">
                Fiabilité
              </TableCell>
              {filteredSeances.map(({ session }) => (
                <TableCell key={session.order} className="border border-gray-300 px-3 py-2 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    session.fiable 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {session.fiable ? 'Fiable' : 'Non fiable'}
                  </span>
                </TableCell>
              ))}
            </TableRow>

            {/* Other parameters */}
            {uniqueParamsArray
              .filter(param => param !== "date" && param !== "fiable")
              .map((param) => (
                <TableRow key={param} className="hover:bg-gray-50">
                  <TableCell className="border border-gray-300 px-3 py-2 font-medium text-gray-700">
                    {getParameterLabel(param)}
                  </TableCell>
                  {filteredSeances.map(({ dynamicParams, session }) => (
                    <TableCell 
                      key={session.order} 
                      className="border border-gray-300 px-3 py-2 text-center text-sm"
                    >
                      {formatValue(param, dynamicParams.content[param] || "")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SeanceTable;
